import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Map as MapIcon, Home as HomeIcon, Compass, ArrowRight, ArrowLeft } from "lucide-react";

const TOUR_LS_KEY = "fromhere_tour_v1";
const STEPS = [
    {
        Icon: MapIcon,
        title: "This is your local map",
        body: "Every dot is a place near you — eat & drink, shops, services, makers & artists, markets or gigs. Toggle the layers up top to hide the ones you don't care about.",
    },
    {
        Icon: HomeIcon,
        title: "Set a home pin so search works",
        body: "Search is scoped to your radius — no random cities in the results. Set a home pin from the top-right avatar menu, or tap 'From me' to use your current location once.",
    },
    {
        Icon: Compass,
        title: "Explore anywhere",
        body: "Planning a trip? Use 'Explore ▸ Explore a location' to drop into another city and browse pins there. Save it as a trip to come back later.",
    },
];

export default function OnboardingTour() {
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState(0);

    useEffect(() => {
        try {
            if (!localStorage.getItem(TOUR_LS_KEY)) {
                // Delay so the map renders first — tour opens over the top.
                const t = setTimeout(() => setOpen(true), 1200);
                return () => clearTimeout(t);
            }
        } catch { /* private mode */ }
    }, []);

    const dismiss = () => {
        try { localStorage.setItem(TOUR_LS_KEY, "1"); } catch {}
        setOpen(false);
        // Broadcast so anything waiting on the tour to finish (e.g. the geo nudge) can react in the same session.
        try { window.dispatchEvent(new Event("fromhere:tour-done")); } catch {}
    };
    const next = () => (step < STEPS.length - 1 ? setStep(step + 1) : dismiss());
    const back = () => setStep((s) => Math.max(0, s - 1));
    const s = STEPS[step];
    const Icon = s.Icon;

    return (
        <Dialog open={open} onOpenChange={(v) => !v && dismiss()}>
            <DialogContent className="max-w-md" data-testid="onboarding-tour">
                <VisuallyHidden asChild><DialogTitle>Welcome to From Here</DialogTitle></VisuallyHidden>
                <VisuallyHidden asChild><DialogDescription>Three-step tour of the map and search.</DialogDescription></VisuallyHidden>
                <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-2">
                    <Icon size={20} />
                </div>
                <div className="text-xs uppercase tracking-[0.25em] font-bold text-muted-foreground">
                    Welcome · Step {step + 1} of {STEPS.length}
                </div>
                <h2 className="font-display text-2xl font-black tracking-tight mt-1">{s.title}</h2>
                <p className="text-sm text-muted-foreground mt-2">{s.body}</p>
                <div className="flex gap-1.5 mt-4">
                    {STEPS.map((_, i) => (
                        <div key={i}
                            className={`h-1 rounded-full flex-1 transition-colors ${i === step ? "bg-primary" : "bg-secondary"}`}/>
                    ))}
                </div>
                <DialogFooter className="mt-2 gap-2 sm:gap-2">
                    {step > 0 && (
                        <Button variant="outline" onClick={back} className="rounded-full"
                            data-testid="tour-back">
                            <ArrowLeft size={13} className="mr-1" /> Back
                        </Button>
                    )}
                    <Button onClick={next} data-testid="tour-next"
                        className="rounded-full bg-primary hover:bg-primary/90 hover:text-white">
                        {step < STEPS.length - 1 ? <>Next <ArrowRight size={13} className="ml-1" /></> : "Explore the map"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
